import { createContext, useContext, useState, useEffect } from 'react';

const HydrationContext = createContext(false);

/**
 * HydrationProvider — tracks whether the app is currently hydrating
 * prerendered content. Components use `useIsHydrating()` to render
 * static/deterministic HTML during hydration, then enable animations
 * and below-fold content after hydration completes.
 *
 * Three modes:
 * - Puppeteer capture (__PRERENDER__=true): isHydrating stays true forever
 * - Client hydration (__HYDRATING__=true): isHydrating starts true, becomes false after mount
 * - Dev mode (neither flag): isHydrating starts false, everything renders normally
 */
export function HydrationProvider({ children }) {
  const initial = typeof window !== 'undefined' && (window.__HYDRATING__ || window.__PRERENDER__);
  const [isHydrating, setIsHydrating] = useState(!!initial);

  useEffect(() => {
    if (isHydrating && !window.__PRERENDER__) {
      setIsHydrating(false);
    }
  }, [isHydrating]);

  return (
    <HydrationContext.Provider value={isHydrating}>
      {children}
    </HydrationContext.Provider>
  );
}

export function useIsHydrating() {
  return useContext(HydrationContext);
}
